import * as React from "react"
import { cn } from "../../../lib/utils"

interface DatatableProps extends React.HTMLAttributes<HTMLTableElement> {
  containerClassName?: string;
}

const Datatable = React.forwardRef<HTMLTableElement, DatatableProps>(
  ({ className, containerClassName, ...props }, ref) => (
    <div className={cn("relative w-full overflow-x-auto border border-border rounded-lg", containerClassName)}>
      <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm font-sans font-[var(--font-family--sans)]", className)}
        {...props}
      />
    </div>
  )
)
Datatable.displayName = "Datatable"

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn("sticky top-0 z-30 bg-[var(--table-header-bg)]", className)} {...props} />
  )
)
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
  )
)
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot
      ref={ref}
      className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
      {...props}
    />
  )
)
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b transition-colors hover:bg-[var(--table-cell-bg-hover)] data-[state=selected]:bg-[var(--table-cell-bg-selected)]",
        className
      )}
      {...props}
    />
  )
)
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        `sticky top-0 z-30 text-left align-middle
        min-h-9 px-[var(--semantic-s)] py-[var(--semantic-xs)]
        font-sans text-sm font-semibold leading-[1.2] tracking-normal
        text-[var(--general-text-secondary)]
        bg-[var(--table-header-bg)] hover:bg-[var(--table-header-bg-hover)]
        border-b border-border
        [&:has([role=checkbox])]:pr-0`,
        className
      )}
      {...props}
    />
  )
)
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn("px-4 py-3 align-middle text-sm text-foreground border-b border-border [&:has([role=checkbox])]:pr-0", className)}
      {...props}
    />
  )
)
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn("mt-4 text-xs text-muted-foreground", className)} {...props} />
  )
)
TableCaption.displayName = "TableCaption"

export { Datatable, TableHeader, TableBody, TableFooter, TableRow, TableHead, TableCell, TableCaption }
